import { ReactNode } from 'react';
import { Box, Link, Popover, ProgressBar, SpaceBetween } from '@amzn/awsui-components-react';
import { useLocalizedText } from '../../hooks/localizedText';
import { DualValueDisplay } from './displayUtils';
import './activitySummary.scss';

interface SummaryTileProps {
  label: string;
  info?: ReactNode;
  children: ReactNode;
}

function SummaryTile({ label, info, children }: SummaryTileProps) {
  return (
    <SpaceBetween size="s">
      <SpaceBetween direction="horizontal" size="xs" alignItems="center">
        <Box variant="awsui-key-label">{label}</Box>
        {info}
      </SpaceBetween>
      {children}
    </SpaceBetween>
  );
}

export function SingleValueTile({ label, value }: { label: string; value: string }) {
  return (
    <SummaryTile label={label}>
      <Box variant="awsui-value-large">{value}</Box>
    </SummaryTile>
  );
}

export interface DualValueTileProps {
  label: string;
  display: DualValueDisplay;
  formatValue: (value: number) => string;
}

/** This-year value with "in {year}", and an "{allTime} all time" line, per the LLD dual-value rules. */
export function DualValueTile({ label, display, formatValue }: DualValueTileProps) {
  const { t } = useLocalizedText(['activity']);

  return (
    <SummaryTile label={label}>
      <div>
        <span className="activity-summary-dual-value">
          <Box variant="awsui-value-large">{formatValue(display.primary)}</Box>
          {display.year !== undefined && (
            <Box variant="small" color="text-body-secondary">
              {t('activitySummary.inYear', { year: String(display.year) })}
            </Box>
          )}
        </span>
        {display.allTime !== undefined && (
          <Box variant="small" color="text-body-secondary">
            {t('activitySummary.allTime', { value: formatValue(display.allTime) })}
          </Box>
        )}
      </div>
    </SummaryTile>
  );
}

export interface TrackingRateTileProps {
  label: string;
  percent: number;
  // Popover body explaining the calculation, which differs between the two summaries.
  infoContent: string;
}

/**
 * Stock ProgressBar with an Info link by the label that opens a medium Popover (OQ-15: no status
 * icon after the bar).
 */
export function TrackingRateTile({ label, percent, infoContent }: TrackingRateTileProps) {
  const { t } = useLocalizedText(['activity', 'translation']);

  const info = (
    <Popover
      triggerType="custom"
      size="medium"
      dismissAriaLabel={t('activitySummary.closeInfo')}
      content={infoContent}
    >
      <Link variant="info" ariaLabel={t('activitySummary.infoAriaLabel', { label })}>
        {t('activitySummary.info')}
      </Link>
    </Popover>
  );

  return (
    <SummaryTile label={label} info={info}>
      <ProgressBar value={percent} ariaLabel={label} />
    </SummaryTile>
  );
}
